import { useRef } from 'react';
import { DragSourceMonitor, DropTargetMonitor, useDrag, useDrop, XYCoord } from 'react-dnd';
import Column from '../../../models/Column';
import { ItemType } from '../Table';

export interface DraggableThProps {
  index: number;
  column: Column;
  moveHeader: (dragIndex: number, hoverIndex: number) => void;
  sortHandler: (c: Column) => void;
  onDrop: (dropIndex: number, draggedId: string) => void;
}

interface DragItem {
  index: number;
  id: string;
  type: string;
}

const sortClassName = (column: Column) => {
  if (!column.sortable) {
    return '';
  }
  if (!column.isSorted) {
    return 'sortable';
  }
  return `sort-${column.sortDirection}`;
};

const DraggableTh = (props: DraggableThProps) => {
  const { index, column, moveHeader, sortHandler, onDrop } = props;

  const sortByColumn = (columnToSort: Column): (() => void) => {
    if (columnToSort.sortable) {
      return () => {
        sortHandler(columnToSort);
      };
    }
    return () => {
      // empty
    };
  };

  const ref = useRef<HTMLTableHeaderCellElement>(null);
  const [, drop] = useDrop({
    accept: ItemType.TH,
    hover: (item: DragItem, monitor: DropTargetMonitor) => {
      if (!ref.current) {
        return;
      }

      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRectangle = ref.current.getBoundingClientRect();
      const hoverMiddleX = (hoverBoundingRectangle.right - hoverBoundingRectangle.left) / 2;

      const clientOffset = monitor.getClientOffset();

      const hoverClientX = (clientOffset as XYCoord).x - hoverBoundingRectangle.left;

      if (dragIndex < hoverIndex && hoverClientX < hoverMiddleX) {
        return;
      }

      if (dragIndex > hoverIndex && hoverClientX > hoverMiddleX) {
        return;
      }

      moveHeader(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
    drop: (item: DragItem) => {
      onDrop(index, item.id);
    },
  });

  const toolTipText =
    'When there are multiple Residency States for a policy/account the Basis & Amount Paid fields ' +
    'are a total for all states included on the policy/account. ' +
    'Where the Compensation Rate is a blended rate of all states included on the policy/account, using the calculation formula below:\n' +
    '\n' +
    'Premium for State1 * State 1 Compensation Rate = Earning for State 1\n' +
    '\n' +
    'Premium for State 2 * State 2 Compensation Rate  = Earning for State 2\n' +
    '\n' +
    'State 1 Earning  + State 2 Earning  = Amount Paid  \n' +
    '\n' +
    'State 1 Premium + State 2 Premium = Basis\n' +
    '\n' +
    'Amount Paid  / Basis  = blended rate displayed in Compensation Rate';

  const [, drag] = useDrag({
    item: {
      type: ItemType.TH,
      id: column.id,
      index,
    },
    collect: (monitor: DragSourceMonitor) => ({ isDragging: monitor.isDragging() }),
  });

  drag(drop(ref));
  return (
    <th
      ref={ref}
      role="columnheader"
      key={column.id}
      className={sortClassName(column)}
      title={column.tooltipText}
      onClick={sortByColumn(column)}
    >
      {column.header}
      {column.className === 'newline' && (
        <a href="# " data-toggle="tooltip" title={toolTipText} aria-label="tooltip">
          <i className="fa fa-info-circle help-tip" />
        </a>
      )}
    </th>
  );
};

export default DraggableTh;
